import { useState, useEffect, useMemo, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { skipToken } from "@reduxjs/toolkit/query";

import { userRoleSchema } from "../../yup";
import style from "./user.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import {
  useGetRoleAPIServicesQuery,
  useGetServicesQuery,
  useGetUserRoleDetailsQuery,
  useUpdateUserRoleDetailsMutation,
} from "../../services/api";
import { Input } from "../../components/UserInput";
import { isRoleApiServicesResponse, isUserRole } from "./userShared";

/** Form values inferred from the user role schema. */
type FormState = typeof userRoleSchema.__outputType;

/** API service payload shape used in role-service assignments. */
type ApiService = {
  id: number | string;
  endpoint_name: string;
};

/** Select option for service autocomplete. */
type ServiceOption = {
  id: number | string;
  label: string;
  value: string;
};

const isApiServiceArray = (value: unknown): value is ApiService[] =>
  Array.isArray(value);

type RoleApiServicesResponse = {
  api_services?: ApiService[];
};

/**
 * Extracts a user-friendly error message from an API error response.
 *
 * Args:
 *   error: Unknown error value from the mutation.
 *
 * Returns:
 *   string: Display-friendly error message.
 */
const getUpdateErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null) {
    const maybeError = error as { data?: { detail?: unknown } };
    if (maybeError.data?.detail != null) {
      return String(maybeError.data.detail);
    }
  }
  return "Please enter valid details";
};

/**
 * Renders the edit user role page and updates role service assignments.
 *
 * Returns:
 *   JSX.Element: Edit form with service selection and save actions.
 *
 * Notes:
 *   TODO: Confirm whether the form should block when role id is invalid.
 */
const EditUserRole = () => {
  const [updateUserRole] = useUpdateUserRoleDetailsMutation();
  const navigation = useNavigate();
  const { id } = useParams();
  const roleId = Number(id);
  const hasValidId = Number.isFinite(roleId) && roleId > 0;
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isUpdatingUserRole, setIsUpdatingUserRole] = useState(false);

  const servicesQuery = useGetServicesQuery("");
  const apiServices = isApiServiceArray(servicesQuery.data)
    ? servicesQuery.data
    : undefined;

  // Skip queries when route params are invalid to avoid unnecessary requests.
  const userRoleDetailsQuery = useGetUserRoleDetailsQuery(
    hasValidId ? id ?? "" : skipToken
  );
  const userRoleDetails = isUserRole(userRoleDetailsQuery.data)
    ? userRoleDetailsQuery.data
    : undefined;

  const roleApiServicesQuery = useGetRoleAPIServicesQuery(
    hasValidId ? { userRoleId: roleId } : skipToken
  );
  const roleApiServices = isRoleApiServicesResponse<ApiService>(
    roleApiServicesQuery.data
  )
    ? roleApiServicesQuery.data
    : undefined;
  const { isLoading, isFetching } = roleApiServicesQuery;

  const services = useMemo<ServiceOption[]>(
    () =>
      (apiServices ?? []).map((service) => ({
        id: service.id,
        label: service.endpoint_name,
        value: service.endpoint_name,
      })),
    [apiServices]
  );

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(userRoleSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    reset,
  } = useForm<FormState>({
    resolver: yupResolver(userRoleSchema),
    mode: "onChange",
  });

  useEffect(() => {
    // Reset form once both role details and services arrive to avoid partial form state.
    if (!isEmpty(userRoleDetails) && !isEmpty(apiServices)) {
      reset({
        role_name: userRoleDetails?.name ?? "",
        endpoint_names:
          roleApiServices?.api_services?.map((service) => {
            return {
              id: service?.id,
              label: service?.endpoint_name,
              value: service?.endpoint_name,
            };
          }) ?? [],
      });
    }
  }, [apiServices, reset, roleApiServices, userRoleDetails]);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Submits service assignment changes for the selected role.
   *
   * Args:
   *   values: Validated form payload.
   *
   * Returns:
   *   Promise<void>: Resolves after updates and navigation.
   */
  const onSubmit = async (values: FormState) => {
    if (!hasValidId) {
      setMessage({
        type: "error",
        text: "Invalid user role id.",
      });
      return;
    }
    try {
      setIsUpdatingUserRole(true);
      setMessage(null);
      const { endpoint_names } = values;

      // Diff selected vs existing endpoints so we only submit add/remove deltas.
      const selectedEndpointNames =
        endpoint_names?.map((service) => service.value) ?? [];
      const existingEndpointNames =
        roleApiServices?.api_services?.map((service) => service?.endpoint_name) ??
        [];
      const selectedSet = new Set(selectedEndpointNames);
      const existingSet = new Set(existingEndpointNames);

      const added = selectedEndpointNames.filter(
        (endpoint) => !existingSet.has(endpoint)
      );
      const removed = existingEndpointNames.filter(
        (endpoint) => !selectedSet.has(endpoint)
      );

      const requests: Promise<unknown>[] = [];
      if (added.length > 0) {
        requests.push(
          updateUserRole({
            role_id: roleId,
            endpoint_names: added,
            is_remove: false,
          }).unwrap()
        );
      }
      if (removed.length > 0) {
        requests.push(
          updateUserRole({
            role_id: roleId,
            endpoint_names: removed,
            is_remove: true,
          }).unwrap()
        );
      }

      if (requests.length === 0) {
        // No changes detected; avoid a no-op update and exit cleanly.
        // No delta means no API calls; exit early to avoid a no-op update.
        setIsUpdatingUserRole(false);
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }
        navigation(-1);
        return;
      }

      await Promise.all(requests);
      setIsUpdatingUserRole(false);
      setMessage({
        type: "success",
        text: "User role updated successfully.",
      });
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getUpdateErrorMessage(error),
      });
      setIsUpdatingUserRole(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading2}
            sx={{ marginTop: "10px" }}
          >
            User Role Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="Eg. Super Admin"
                label="Role Name"
                required
                register={register}
                name="role_name"
                errors={errors}
                value={userRoleDetails?.name ?? ""}
                disabled={true}
              />
            </Box>
            <Box className={style.row} sx={{ flexDirection: "column" }}>
              <Typography
                className={style.inputLabel}
                sx={{ marginBottom: -2, display: "flex" }}
              >
                Services
                <Typography sx={{ color: "red", marginLeft: 0.7 }}>
                  {" *"}
                </Typography>
              </Typography>
              <Controller
                control={control}
                name={"endpoint_names"}
                rules={{ required: true }}
                render={({ field: { onChange, value }, ...props }) => (
                  <Autocomplete<ServiceOption, true, true, false>
                    {...props}
                    sx={{
                      width: "100%",
                    }}
                    multiple
                    disablePortal
                    disableCloseOnSelect
                    id="tags-outlined"
                    options={services ?? []}
                    disableClearable={true}
                    value={value ?? []}
                    onChange={(_, nextValue) => {
                      onChange(nextValue);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={
                          isEmpty(getValues("endpoint_names"))
                            ? "Select api services"
                            : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    )}
                  />
                )}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons} sx={{ marginBottom: 3 }}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Cancel"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              disabled={isFetching || isLoading}
              loading={isUpdatingUserRole}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditUserRole;
